<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import useSharedStore from '@/presentation/store/shared';
import NavbarItem from '@/presentation/components/layouts/default/NavbarItem.vue';
import CurvedBottomNavbar from '@/presentation/components/layouts/default/CurvedBottomNavbar.vue';
import HomeNavbar from '@/presentation/components/home/navbar/HomeNavbar.vue';
import SiteFooter from '@/presentation/components/common/site-footer/SiteFooter.vue';
import BaseImg from '@/presentation/components/base/image/BaseImg.vue';
import { useMainNavigationItems, type MainNavigationItem } from '@/presentation/layouts/useMainNavigationItems';
import BusinessAppTour from '@/presentation/components/common/business-tour/BusinessAppTour.vue';
import PwaInstallSuggestion from '@/presentation/components/common/pwa-install/PwaInstallSuggestion.vue';
import { useMutation } from '@/presentation/composables';
import { useRequestStore } from '@/presentation/modules/request';

const route = useRoute();
const routeName = computed(() => String(route.name || ''));
const sharedStore = useSharedStore();
const requestStore = useRequestStore();
const userInformation = computed(() => sharedStore.userInformation);
const businessInfo = computed(() => sharedStore.businessInfo);

const bottomNavbarItems = useMainNavigationItems(route, userInformation, businessInfo);

const { mutate: fetchPendingReservations, data: pendingReservations } = useMutation({
	mutationFn: requestStore.getPendReservePreviewInBusinessBook,
});

const extractList = (payload: any): any[] => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data?.data)) return payload.data.data;
	if (Array.isArray(payload?.data)) return payload.data;
	return [];
};

const hasPendingReservations = computed(() => extractList(pendingReservations.value).length > 0);

const isBusinessDashboard = computed(() => {
	return (
		userInformation.value?.role === 'Business' &&
		(route.path.startsWith('/dashboard') || route.path.startsWith('/admin') || route.path.startsWith('/requests/detail'))
	);
});

const BUSINESS_MOBILE_NAVIGATION_ORDER = ['BookAppointment', 'DashboardCustomers', 'Index', 'DashboardWallet', 'DashboardSetting'];
const USER_MOBILE_NAVIGATION_ORDER = ['Home', 'Explore', 'Category', 'Request', 'Setting'];

const navigationItems = computed<MainNavigationItem[]>(() =>
	bottomNavbarItems.value.map((item) => (item.routeName === 'BookAppointment' ? { ...item, hasBadge: hasPendingReservations.value } : item)),
);

const mobileNavigationItems = computed(() => {
	const role = userInformation.value?.role || 'AllUser';
	const visibleItems = navigationItems.value.filter((item) => item.activeForRule.includes(role) && !item.hiddenInMobileForRule?.includes(role));

	if (role === 'Business' && isBusinessDashboard.value) {
		return [...visibleItems].sort(
			(firstItem, secondItem) => BUSINESS_MOBILE_NAVIGATION_ORDER.indexOf(firstItem.routeName) - BUSINESS_MOBILE_NAVIGATION_ORDER.indexOf(secondItem.routeName),
		);
	}

	// For general public / customer users
	return [...visibleItems].sort(
		(firstItem, secondItem) => USER_MOBILE_NAVIGATION_ORDER.indexOf(firstItem.routeName) - USER_MOBILE_NAVIGATION_ORDER.indexOf(secondItem.routeName),
	);
});

const invertedNavbarItems = computed(() => [...navigationItems.value].reverse());

const refreshPendingReservationsBadge = () => {
	if (userInformation.value?.role === 'Business') fetchPendingReservations();
};

watch(
	() => userInformation.value?.role,
	(role) => {
		if (role === 'Business') refreshPendingReservationsBadge();
	},
	{ immediate: true },
);

onMounted(() => {
	window.addEventListener('newty:pending-reservations-updated', refreshPendingReservationsBadge);
});

onBeforeUnmount(() => {
	window.removeEventListener('newty:pending-reservations-updated', refreshPendingReservationsBadge);
});
</script>

<template>
	<!-- Business Dashboard Layout Mode -->
	<div v-if="isBusinessDashboard" class="default-layout-root min-h-screen bg-gray-100 md:flex" dir="rtl">
		<main
			data-business-tour="content"
			class="w-full min-h-screen pb-[calc(var(--app-bottom-nav-height,5rem)+1.5rem)] mx-auto bg-gray-100 md:mr-[260px] md:py-10 md:px-8 md:overflow-hidden"
		>
			<slot />
		</main>
		<nav
			data-business-tour="desktop-nav"
			class="hidden md:flex flex-col fixed right-0 top-0 z-20 w-[260px] h-screen border-l border-slate-100 bg-white/95 pb-10 shadow-[0_16px_44px_rgba(15,23,42,0.06)] backdrop-blur"
		>
			<div class="px-8 py-6">
				<BaseImg src="images/newty-logo.png" src-type="public" width="100" class="mx-auto" />
			</div>
			<div class="flex flex-col gap-1">
				<template v-for="item in invertedNavbarItems" :key="item.routeName">
					<NavbarItem
						v-if="item.activeForRule.includes(userInformation.role)"
						:label="item.label"
						:icon-name="item.iconName"
						:active-route-name="routeName"
						:route-name="item.routeName"
						:route-address="item.routeAddress"
						:custom-rule-activation="item.customRuleActivation"
						:has-badge="item.hasBadge"
					/>
				</template>
			</div>
		</nav>

		<!-- Mobile Curved Bottom Navbar -->
		<CurvedBottomNavbar :items="mobileNavigationItems" :active-route-name="routeName" />

		<PwaInstallSuggestion />
		<BusinessAppTour />
	</div>

	<!-- Default Layout for Public & Customer Pages (matching Home layout) -->
	<div v-else dir="rtl" class="default-layout-root min-h-screen bg-[#f8faff] text-slate-900 selection:bg-[#4848FE]/15 selection:text-[#4848FE]">
		<!-- Modern Sticky Top Header -->
		<HomeNavbar />

		<!-- Main Content Slot with bottom clearance for mobile navbar -->
		<main data-business-tour="content" class="w-full pb-8 md:pb-10">
			<slot />
		</main>

		<!-- Site Footer with bottom padding ensuring full clearance on mobile -->
		<SiteFooter />

		<!-- Mobile Curved Bottom Navbar -->
		<CurvedBottomNavbar :items="mobileNavigationItems" :active-route-name="routeName" />

		<PwaInstallSuggestion />
		<BusinessAppTour />
	</div>
</template>

<style scoped>
.default-layout-root {
	--app-bottom-nav-height: calc(5rem + env(safe-area-inset-bottom, 0px));
}

@media (min-width: 768px) {
	.default-layout-root {
		--app-bottom-nav-height: 0px;
	}
}
</style>
